<template>
  <Stack>
    <Alert v-if="tenant.theme.portal_notification_message" type="info">
      <p class="text-sm text-blue-700" v-html="tenant.theme.portal_notification_message"></p>
    </Alert>

    <Alert v-if="has_company_user_account" type="info" title="Aryeo Admin Login">
      <Typography
        >It looks you may be trying to login to the Aryeo Admin Portal. If so, please continue with
        your login at
        <a
          :href="route('web.company.login.show')"
          class="text-primary hover:text-primary-darker font-bold"
          >app.aryeo.com/login</a
        >.
      </Typography>
    </Alert>

    <Stack space="xsmall">
      <Columns :space="['xsmall', 'gutter']" collapseBelow="md">
        <Column width="1/2">
          <FormTextInput
            :error="forError('first_name')"
            :value="form.first_name"
            label="First name"
            name="first_name"
            type="text"
            @update:modelValue="updateFormValue"
          />
        </Column>
        <Column width="1/2">
          <FormTextInput
            :error="forError('last_name')"
            :value="form.last_name"
            label="Last name"
            name="last_name"
            type="text"
            @update:modelValue="updateFormValue"
          />
        </Column>
      </Columns>
      <Columns :space="['xsmall', 'gutter']" collapseBelow="md">
        <Column width="1/2">
          <FormTextInput
            :error="forError('email')"
            :value="form.email"
            label="Email"
            name="email"
            type="email"
            @update:modelValue="updateFormValue"
          />
        </Column>
        <Column width="1/2">
          <FormPhoneInput
            :error="forError('phone_number')"
            :value="form.phone"
            label="Phone number"
            name="phone_number"
            type="tel"
            @update:modelValue="updateFormValue"
          />
        </Column>
      </Columns>
      <Columns :space="['xsmall', 'gutter']" collapseBelow="md">
        <Column width="1/2">
          <FormTextInput
            :error="forError('brokerage_name')"
            :value="form.brokerage_name"
            label="Brokerage, team, or office name"
            name="brokerage_name"
            type="text"
            @update:modelValue="updateFormValue"
          />
        </Column>
        <Column width="1/2" v-if="company_has_customer_teams_with_affiliate_id">
          <FormTextInput
            :error="forError('affiliate_id')"
            :value="form.affiliate_id"
            label="Affiliate ID"
            is-optional
            name="affiliate_id"
            type="text"
            @update:modelValue="updateFormValue"
          />
        </Column>
      </Columns>
      <Columns :space="['xsmall', 'gutter']" collapseBelow="md">
        <Column width="1/2">
          <FormTextInput
            :error="forError('password')"
            :value="form.password"
            label="Password"
            name="password"
            type="password"
            @update:modelValue="updateFormValue"
          />
        </Column>
        <Column width="1/2">
          <FormTextInput
            :error="forError('password_confirm')"
            :value="form.password_confirmation"
            label="Confirm password"
            name="password_confirmation"
            type="password"
            @update:modelValue="updateFormValue"
          />
        </Column>
      </Columns>
    </Stack>

    <Columns
      align="stretch"
      alignY="center"
      collapseBelow="md"
      space="gutter"
      :isReversed="[false, false, true]"
    >
      <Column width="content">
        <Button :isLoading="form.processing" type="submit" text="Create Account" @click="submit" />
      </Column>
    </Columns>

    <aryeo-link :href="route('web.customer.portal.show')">
      <p class="text-sm font-medium leading-5 text-gray-700 cursor-pointer hover:text-gray-500">
        &larr; Back to Portal Home
      </p>
    </aryeo-link>
  </Stack>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'
import { Column, Columns, FormTextInput, Stack, Button, Typography } from '@aryeo/runway'
import { useErrors } from '@/Hooks/Shared/useErrors.js'
import { useTenant } from '@/Hooks/Shared/useTenant.js'

import Layout from '@/Layouts/Customer/AuthCustomerLayout.vue'
import Alert from '@/Runway/Alerts/Simple.vue'
import FormPhoneInput from '@/Runway/Forms/FormInputs/FormPhoneInput.vue'

defineOptions({
  layout: Layout,
})

const props = defineProps({
  email: {
    type: String,
  },
  has_company_user_account: {
    type: Boolean,
    default: false,
  },
  company_has_customer_teams_with_affiliate_id: {
    type: Boolean,
    default: false,
  },
})

const { tenant } = useTenant()

const { forError } = useErrors()

const form = useForm({
  first_name: null,
  last_name: null,
  phone_number: null,
  email: props.email,
  brokerage_name: null,
  affiliate_id: null,
  password: null,
  password_confirmation: null,
})

const updateFormValue = ({ key, value }) => {
  form[key] = value
}

const submit = () => {
  form.post(route('web.customer.registration.store'))
}
</script>
